import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ISPAConfig } from "config/api";
import { ToastContext } from "context/ToastContext";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { FinixPaymentSection } from "pages/OrderOverview/components/FinixPaymentSection/FinixPaymentSection";
import { PaymentSection } from "pages/OrderOverview/components/PaymentSection";
import { useEffect, useMemo, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useCreateOrderPaymentIntentMutation } from "store/queries/order";
import { IPaymentPlan } from "types/Payment";

import ErrorMessage from "../ErrorMessage";

const getPublishableKey = () => {
  const config = window?.config as ISPAConfig;

  if (!config) {
    throw new Error("Config not found");
  }

  return config.stripe.publishableKey;
};

function StripePaymentPlan({
  expirationDays,
  orderInfo,
  showPaymentSection,
  setShowPaymentSection,
  clearPrice,
  totalSaving,
  fullPrice,
}: any) {
  const { id: externalId } = useParams();
  const { current: toastElement } = useContext(ToastContext);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    setStripePromise(loadStripe(getPublishableKey()));
  }, []);

  const [createOrderPaymentIntent, { isLoading: isStripeLoading }] =
    useCreateOrderPaymentIntentMutation();

  const [clientSecret, setClientSecret] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    createOrderPaymentIntent({
      orderId: externalId || "",
      paymentPlan: true,
      payRemainingPrice: false,
    })
      .unwrap()
      .then(
        ({
          data,
          stripeCustomerId: responseStripeCustomerId,
        }: {
          data: any;
          stripeCustomerId: string;
        }) => {
          setClientSecret(data.client_secret);
          setStripeCustomerId(responseStripeCustomerId);
          setPaymentInitError(null);
        }
      )
      .catch((error) => {
        console.error("Error creating payment intent:", error);
        const errorMessage =
          error?.data?.messages?.[0] ||
          "There was an issue setting up your payment plan. Please try again.";
        setPaymentInitError(errorMessage);

        toastElement?.show({
          severity: "error",
          summary: "Payment Plan Setup Error",
          detail: errorMessage,
        });
      });
  }, [externalId, isRetrying]);

  const handleRetry = () => {
    setIsRetrying(!isRetrying); // Toggle to trigger useEffect
  };

  // Show error UI if payment intent creation failed
  if (paymentInitError) {
    return (
      <ErrorMessage
        paymentInitError={paymentInitError}
        buttonDisabled={isStripeLoading}
        onTryAgain={handleRetry}
      />
    );
  }

  if (!clientSecret?.length) {
    return <LoadingSpinner />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        // HACK TO HIDE THE TERMS TEXT IN STRIPE EMBEDDED ELEMENT
        appearance: {
          rules: {
            ".TermsText": {
              color: "transparent",
              fontSize: "0px",
            },
          },
        },
      }}
    >
      <PaymentSection
        expirationDays={expirationDays}
        order={orderInfo}
        prices={{ clearPrice, totalSaving, fullPrice }}
        onReturnClick={() => {
          setShowPaymentSection({
            paymentPlan: false,
            paymentSection: false,
            paymentSuccess: false,
          });
        }}
        showPaymentSection={showPaymentSection}
        setShowPaymentSection={setShowPaymentSection}
        stripeCustomerId={stripeCustomerId}
        isDownPaymentOnly={
          orderInfo?.paymentPlan?.find((plan: IPaymentPlan) => plan?.active)
            ?.isDownPaymentOnly
        }
      />
    </Elements>
  );
}

export default function PaymentPlan({
  expirationDays,
  orderInfo,
  showPaymentSection,
  setShowPaymentSection,
  clearPrice,
  totalSaving,
  fullPrice,
  onUpdateOrder,
}: any) {
  const { isFinix, isLoading, isError, finixApplicationId, finixEnvironment } =
    useFinixPaymentProvider(orderInfo);

  const activePaymentPlan = useMemo(
    () => orderInfo?.paymentPlan?.find((plan: IPaymentPlan) => plan?.active),
    [orderInfo]
  );

  if (isFinix) {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (isError || !finixApplicationId) {
      return (
        <ErrorMessage
          paymentInitError="Unable to load payment provider. Please refresh and try again."
          onTryAgain={() => window.location.reload()}
          buttonDisabled={false}
        />
      );
    }

    return (
      <FinixPaymentSection
        expirationDays={expirationDays}
        order={orderInfo}
        prices={{ clearPrice, totalSaving, fullPrice }}
        finixApplicationId={finixApplicationId}
        finixEnvironment={finixEnvironment}
        showPaymentSection={showPaymentSection}
        setShowPaymentSection={setShowPaymentSection}
        onReturnClick={() => {
          setShowPaymentSection({
            paymentPlan: false,
            paymentSection: false,
            paymentSuccess: false,
          });
        }}
        onUpdateOrder={onUpdateOrder}
        isPaymentPlan
        isDownPaymentOnly={activePaymentPlan?.isDownPaymentOnly}
      />
    );
  }

  return (
    <StripePaymentPlan
      expirationDays={expirationDays}
      orderInfo={orderInfo}
      showPaymentSection={showPaymentSection}
      setShowPaymentSection={setShowPaymentSection}
      clearPrice={clearPrice}
      totalSaving={totalSaving}
      fullPrice={fullPrice}
    />
  );
}
